import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

function Explore() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiService.getStats();
      setStats(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const totalAssociations = (stats?.SideEffect || 0) + 
                            (stats?.DiseaseIndication || 0) + 
                            (stats?.PhenotypeIndication || 0) + 
                            (stats?.DrugIndication || 0) + 
                            (stats?.DiseaseContraindication || 0) + 
                            (stats?.PhenotypeContraindication || 0) + 
                            (stats?.DrugContraindication || 0);

  return (
    <div className="container">
      <h1 style={{ marginBottom: '2rem' }}>Explore Sidekick</h1>

      {/* Overall Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {stats?.SIO_010039 && (
          <div className="card">
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Drug Products
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
              {stats.SIO_010039.toLocaleString()}
            </div>
          </div>
        )}

        {stats?.SIO_010077 && (
          <div className="card">
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Active Ingredients
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
              {stats.SIO_010077.toLocaleString()}
            </div>
          </div>
        )}

        {stats?.DrugCollection && (
          <div className="card">
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Ingredient Combinations
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
              {stats.DrugCollection.toLocaleString()}
            </div>
          </div>
        )}

        {totalAssociations > 0 && (
          <div className="card">
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Total Associations
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
              {totalAssociations.toLocaleString()}
            </div>
          </div>
        )}

        {stats?.DiseaseOrPhenotype && (
          <div className="card">
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Disease/Phenotype Terms
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
              {stats.DiseaseOrPhenotype.toLocaleString()}
            </div>
          </div>
        )}

        {stats?.SIO_000148 && (
          <div className="card">
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              FDA SPL Documents
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
              {stats.SIO_000148.toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Association Breakdown */}
      <div className="card">
        <h2 style={{ marginBottom: '1.5rem' }}>Association Types</h2>
        
        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* Side Effects */}
          {stats?.SideEffect && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>⚠️ Side Effects</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Adverse phenotypes caused by drugs
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--danger)' }}>
                {stats.SideEffect.toLocaleString()}
              </div>
            </div>
          )}

          {/* Disease Indications */}
          {stats?.DiseaseIndication && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>✅ Disease Indications</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Diseases that drugs treat
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--secondary)' }}>
                {stats.DiseaseIndication.toLocaleString()}
              </div>
            </div>
          )}

          {/* Phenotype Indications */}
          {stats?.PhenotypeIndication && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>✅ Phenotype Indications</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Phenotypes that drugs treat
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--secondary)' }}>
                {stats.PhenotypeIndication.toLocaleString()}
              </div>
            </div>
          )}

          {/* Drug Indications */}
          {stats?.DrugIndication && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(59, 130, 246, 0.05)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>💊 Drug Indications</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Recommended drug combinations
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                {stats.DrugIndication.toLocaleString()}
              </div>
            </div>
          )}

          {/* Disease Contraindications */}
          {stats?.DiseaseContraindication && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>🚫 Disease Contraindications</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Diseases where drugs should not be used
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--danger)' }}>
                {stats.DiseaseContraindication.toLocaleString()}
              </div>
            </div>
          )}

          {/* Phenotype Contraindications */}
          {stats?.PhenotypeContraindication && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>🚫 Phenotype Contraindications</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Phenotypes where drugs should not be used
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--danger)' }}>
                {stats.PhenotypeContraindication.toLocaleString()}
              </div>
            </div>
          )}

          {/* Drug Contraindications */}
          {stats?.DrugContraindication && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>🚫 Drug Contraindications</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Drug-drug interactions (do not combine)
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--danger)' }}>
                {stats.DrugContraindication.toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Data Sources */}
      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Data Sources & Ontologies</h2>
        <table>
          <thead>
            <tr>
              <th>Source</th>
              <th>Description</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>FDA SPL Labels</strong></td>
              <td>Structured Product Labels from DailyMed</td>
              <td>
                <a href="https://dailymed.nlm.nih.gov" target="_blank" rel="noopener noreferrer" className="link">
                  DailyMed
                </a>
              </td>
            </tr>
            <tr>
              <td><strong>HPO</strong></td>
              <td>Human Phenotype Ontology</td>
              <td>
                <a href="https://hpo.jax.org" target="_blank" rel="noopener noreferrer" className="link">
                  hpo.jax.org
                </a>
              </td>
            </tr>
            <tr>
              <td><strong>MONDO</strong></td>
              <td>Mondo Disease Ontology</td>
              <td>
                <a href="https://www.ebi.ac.uk/ols/ontologies/mondo" target="_blank" rel="noopener noreferrer" className="link">
                  EBI OLS
                </a>
              </td>
            </tr>
            <tr>
              <td><strong>RxNorm</strong></td>
              <td>Drug nomenclature standard</td>
              <td>
                <a href="https://www.nlm.nih.gov/research/umls/rxnorm" target="_blank" rel="noopener noreferrer" className="link">
                  NLM RxNorm
                </a>
              </td>
            </tr>
            <tr>
              <td><strong>SIO</strong></td>
              <td>Semantic Science Integrated Ontology</td>
              <td>
                <a href="http://semanticscience.org" target="_blank" rel="noopener noreferrer" className="link">
                  SemanticScience.org
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Explore;
